import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Loader2, CheckCircle2, XCircle, MessageSquareWarning, Clock, FileText, ArrowLeft,
  Calendar, CreditCard, Sparkles, AlertTriangle, Receipt,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  sent: "bg-sky-100 text-sky-800",
  viewed: "bg-sky-100 text-sky-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  changes_requested: "bg-amber-100 text-amber-800",
  counter_offered: "bg-fuchsia-100 text-fuchsia-800",
  expired: "bg-gray-200 text-gray-600",
  draft: "bg-gray-100 text-gray-600",
};
const PAYMENT_STYLES = {
  unpaid: "bg-amber-50 text-amber-800 border-amber-200",
  deposit_paid: "bg-sky-50 text-sky-800 border-sky-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  overdue: "bg-rose-50 text-rose-800 border-rose-200",
};
const PAYMENT_LABELS = {
  unpaid: "Unpaid",
  deposit_paid: "Deposit paid",
  paid: "Paid in full",
  overdue: "Overdue",
};

export default function Quote() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(null);
  const [message, setMessage] = useState("");
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [showChangesBox, setShowChangesBox] = useState(false);
  const [showPayBox, setShowPayBox] = useState(false);
  const [showCounterBox, setShowCounterBox] = useState(false);
  const [counterPrice, setCounterPrice] = useState("");
  const [paymentResult, setPaymentResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/quotes/${quoteId}`);
      setQuote(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Quote not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Stripe success redirect (?paid=1&session_id=...)
  useEffect(() => {
    const paid = searchParams.get("paid");
    const sessionId = searchParams.get("session_id");
    if (paid === "1" && sessionId) {
      (async () => {
        try {
          const { data } = await api.post(`/quotes/${quoteId}/finalize-payment?session_id=${encodeURIComponent(sessionId)}`);
          setPaymentResult(data);
          toast.success(`Payment received — booking confirmed (${data.booking_id})`);
          await load();
          if (data.tx_id) {
            setTimeout(() => navigate(`/receipt/${data.tx_id}`), 1500);
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not finalize payment");
        }
      })();
    }
    if (paid === "0") toast.message("Payment cancelled — no charge made.");
  }, [searchParams, quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCheckout = async (payment_type) => {
    if (working) return;
    setWorking(payment_type === "full" ? "pay-full" : "pay-deposit");
    try {
      const returnUrl = window.location.origin;
      const { data } = await api.post(`/quotes/${quoteId}/checkout`, { payment_type, return_url: returnUrl });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setWorking(null);
    }
  };

  const acceptOnly = async () => {
    if (working) return;
    setWorking("accept");
    try {
      const { data } = await api.post(`/quotes/${quoteId}/accept`, { message: null });
      setQuote(data);
      toast.success("Quote accepted — pay when you're ready.");
      setShowPayBox(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept quote");
    } finally { setWorking(null); }
  };

  const act = async (action) => {
    if (working) return;
    setWorking(action);
    try {
      const url = action === "reject" ? `/quotes/${quoteId}/reject` : `/quotes/${quoteId}/request-changes`;
      const { data } = await api.post(url, { message: message || null });
      setQuote(data);
      if (action === "reject") toast.success("Quote rejected. Thanks for letting us know.");
      if (action === "changes") toast.success("Change request sent.");
      setShowRejectBox(false); setShowChangesBox(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setWorking(null); }
  };

  const submitCounter = async () => {
    if (working) return;
    const price = parseFloat(counterPrice);
    if (!price || price <= 0) {
      toast.error("Enter a valid counter-offer price");
      return;
    }
    setWorking("counter");
    try {
      const { data } = await api.post(`/quotes/${quoteId}/counter`, {
        price,
        message: message || null,
      });
      setQuote(data);
      toast.success(`Counter-offer of $${price.toFixed(2)} sent — waiting on JDSN.`);
      setShowCounterBox(false);
      setCounterPrice("");
      setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send counter-offer");
    } finally { setWorking(null); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  }
  if (error || !quote) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-extrabold tracking-tighter font-[Outfit] text-gray-900">{error || "Quote not found"}</h1>
        <Button onClick={() => navigate("/")} className="mt-6 rounded-full">Go home</Button>
      </div>
    );
  }

  const status = quote.status;
  const isAccepted = status === "accepted";
  const isPaid = !!quote.checkout_paid_at;
  const paymentStatus = quote.payment_status || "unpaid";
  const requirePayFirst = !!quote.require_payment_before_scheduling;
  const showPayCTA = !isPaid && !["rejected", "expired"].includes(status);
  const isReadyToPay = isAccepted && !isPaid && (paymentStatus === "unpaid" || paymentStatus === "overdue");
  const allPhotos = [...(quote.before_photos || []), ...(quote.photos || []), ...(quote.after_photos || [])];
  const total = quote.total || quote.price || 0;
  const deposit = total * 0.5;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="quote-page">
      <button onClick={() => navigate("/")} className="inline-flex items-center text-sm text-gray-500 hover:text-[#047857] mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      <div className="bg-gradient-to-br from-[#064E3B] to-[#047857] text-white rounded-3xl p-7 shadow-xl">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-200" />
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-100">JDSN Lawn Care · Quote</div>
        </div>
        <h1 className="mt-1 text-3xl font-extrabold font-[Outfit] tracking-tighter">{quote.title}</h1>
        <p className="mt-1 text-emerald-100 text-sm">Prepared for {quote.customer_name}{quote.address && ` · ${quote.address}`}</p>

        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-5xl font-extrabold tracking-tighter tabular-nums font-[Outfit]">${total.toFixed(2)}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.16em] font-extrabold px-3 py-1.5 rounded-full ${STATUS_STYLES[status] || "bg-white/15 text-white"}`} data-testid="quote-status">
              {String(status).replace(/_/g, " ")}
            </span>
            {(isAccepted || isPaid) && (
              <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-extrabold px-3 py-1.5 rounded-full border ${PAYMENT_STYLES[paymentStatus] || "bg-white/15 text-white border-white/30"}`} data-testid="quote-payment-status">
                {paymentStatus === "overdue" && <AlertTriangle className="w-3 h-3" />}
                {paymentStatus === "paid" && <CheckCircle2 className="w-3 h-3" />}
                {PAYMENT_LABELS[paymentStatus] || paymentStatus}
              </span>
            )}
          </div>
        </div>

        {quote.expires_at && status !== "accepted" && !["rejected","expired"].includes(status) && (
          <div className="mt-3 text-xs text-emerald-100 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Valid until {quote.expires_at.split("T")[0]}
          </div>
        )}

        {isReadyToPay && (
          <div className="mt-4 inline-flex items-center gap-1.5 bg-[#10B981] text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-extrabold shadow-md" data-testid="ready-to-pay-badge">
            <Sparkles className="w-3.5 h-3.5" /> Ready to Pay
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="mt-6 bg-white rounded-3xl border border-gray-200 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Services included</div>
        <ul className="mt-3 divide-y divide-gray-100">
          {quote.items.map((it, idx) => (
            <li key={idx} className="py-3 flex items-start justify-between gap-4" data-testid={`quote-item-${idx}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900">{it.name}</div>
                {it.notes && <div className="text-xs text-gray-600 mt-0.5">{it.notes}</div>}
                {it.estimated_hours && <div className="text-[11px] text-gray-500 mt-0.5">~{it.estimated_hours} hrs</div>}
              </div>
              <div className="text-lg font-extrabold text-[#047857] tabular-nums">${Number(it.price).toFixed(2)}</div>
            </li>
          ))}
        </ul>
        <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Estimated total</div>
          <div className="text-2xl font-extrabold text-gray-900 tabular-nums">${total.toFixed(2)}</div>
        </div>
        {quote.estimated_completion && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-700">
            <Calendar className="w-4 h-4 text-[#047857]" /> Estimated completion: <strong>{quote.estimated_completion}</strong>
          </div>
        )}
        {quote.notes && (
          <div className="mt-4 bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-gray-800 whitespace-pre-wrap">
            {quote.notes}
          </div>
        )}
      </div>

      {/* Photos */}
      {allPhotos.length > 0 && (
        <div className="mt-6 bg-white rounded-3xl border border-gray-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Photos</div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {allPhotos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img src={p} alt={`Quote ${i}`} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Already-paid celebration card */}
      {isPaid && (
        <div className="mt-6 bg-white border-2 border-emerald-200 rounded-3xl p-6" data-testid="quote-paid-card">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-7 h-7 text-[#047857]" />
            <div>
              <div className="text-xl font-extrabold tracking-tight font-[Outfit] text-[#064E3B]">Payment received</div>
              <div className="text-sm text-gray-700">${(quote.checkout_paid_amount || 0).toFixed(2)} paid — booking confirmed.</div>
            </div>
          </div>
          {paymentResult?.tx_id && (
            <Button
              onClick={() => navigate(`/receipt/${paymentResult.tx_id}`)}
              className="mt-4 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
              data-testid="quote-view-receipt"
            >
              <Receipt className="w-4 h-4 mr-1.5" /> View receipt
            </Button>
          )}
        </div>
      )}

      {/* Pay-now / accept actions */}
      {showPayCTA && (
        <div className="mt-6 bg-white rounded-3xl border border-gray-200 p-6" data-testid="quote-actions">
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">
            {isAccepted ? "Complete your booking" : "Your response"}
          </div>

          {!isAccepted && (
            <div className="mt-3 grid sm:grid-cols-3 gap-2">
              <Button
                onClick={() => setShowPayBox(true)}
                disabled={!!working}
                data-testid="quote-pay-now"
                className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold py-6 shadow"
              >
                {(working === "pay-deposit" || working === "pay-full") ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Pay Now</>}
              </Button>
              {!requirePayFirst && (
                <Button
                  onClick={acceptOnly}
                  disabled={!!working}
                  variant="outline"
                  className="rounded-full font-bold py-6 border-2 border-[#047857] text-[#047857] hover:bg-emerald-50"
                  data-testid="quote-accept-only"
                >
                  {working === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept (pay later)</>}
                </Button>
              )}
              <Button
                variant="outline"
                className="rounded-full font-bold py-6"
                onClick={() => { setShowCounterBox(true); setShowChangesBox(false); setShowRejectBox(false); setCounterPrice(String(total.toFixed(2))); }}
                disabled={!!working}
                data-testid="quote-counter"
              >
                <MessageSquareWarning className="w-4 h-4 mr-1.5" /> Counter-offer
              </Button>
            </div>
          )}

          {isAccepted && !isPaid && (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => setShowPayBox(true)}
                disabled={!!working}
                data-testid="quote-pay-now"
                className="flex-1 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold py-6 shadow-lg ring-4 ring-emerald-100"
              >
                {(working === "pay-deposit" || working === "pay-full") ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Pay Now · ${total.toFixed(2)}</>}
              </Button>
              <div className="text-[11px] text-gray-500 italic self-center sm:max-w-[180px]">
                {paymentStatus === "overdue"
                  ? "Quote accepted over 24 hours ago — please complete payment."
                  : "Quote accepted — pay to lock in your appointment."}
              </div>
            </div>
          )}

          {!isAccepted && (
            <div className="mt-2 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => { setShowChangesBox(true); setShowRejectBox(false); setShowCounterBox(false); }}
                disabled={!!working}
                data-testid="quote-changes"
                className="text-[12px] text-gray-600 font-bold underline"
              >
                Or just request changes
              </button>
              <button
                type="button"
                onClick={() => { setShowRejectBox(true); setShowChangesBox(false); setShowCounterBox(false); }}
                disabled={!!working}
                data-testid="quote-reject"
                className="text-[12px] text-rose-700 font-bold underline"
              >
                Or reject this quote
              </button>
            </div>
          )}

          {showPayBox && (
            <div className="mt-4 rounded-2xl bg-emerald-50/60 border-2 border-emerald-200 p-4" data-testid="quote-pay-box">
              <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-emerald-800">Choose how to pay</div>
              <div className="mt-1 text-[12px] text-emerald-900/80">Pay a 50% deposit now to lock your appointment, or pay in full upfront. Booking is confirmed instantly after payment.</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
                <Button
                  onClick={() => startCheckout("deposit")}
                  disabled={!!working}
                  data-testid="quote-pay-deposit"
                  className="rounded-2xl bg-gradient-to-br from-[#0A2E1E] to-[#062818] hover:opacity-90 text-white font-extrabold py-7 flex-col items-center"
                >
                  {working === "pay-deposit" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span className="mt-1.5 text-[15px]">Pay 50% deposit · ${deposit.toFixed(2)}</span>
                      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-300 mt-0.5">balance on completion</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => startCheckout("full")}
                  disabled={!!working}
                  data-testid="quote-pay-full"
                  variant="outline"
                  className="rounded-2xl border-2 border-[#047857] text-[#047857] hover:bg-emerald-50 font-extrabold py-7 flex-col items-center"
                >
                  {working === "pay-full" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span className="mt-1.5 text-[15px]">Pay full · ${total.toFixed(2)}</span>
                      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-700/70 mt-0.5">$0 due on completion</span>
                    </>
                  )}
                </Button>
              </div>
              <button type="button" onClick={() => setShowPayBox(false)} className="mt-3 text-[12px] text-gray-500 font-bold underline" data-testid="quote-pay-cancel">Cancel</button>
            </div>
          )}

          {showCounterBox && (
            <div className="mt-4 rounded-2xl bg-fuchsia-50/60 border-2 border-fuchsia-200 p-4" data-testid="quote-counter-box">
              <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-fuchsia-800">Propose your price</div>
              <div className="mt-1 text-[12px] text-fuchsia-900/80">Enter what you'd like to pay. JDSN can accept, counter back, or decline.</div>
              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 flex items-center rounded-full border-2 border-fuchsia-300 bg-white px-3">
                  <span className="text-fuchsia-700 font-extrabold">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    value={counterPrice}
                    onChange={(e) => setCounterPrice(e.target.value)}
                    className="flex-1 py-2 px-2 outline-none font-bold text-lg text-fuchsia-900 bg-transparent"
                    placeholder="Your offer"
                    data-testid="quote-counter-price"
                  />
                </div>
              </div>
              <Textarea
                placeholder="Optional — a note to JDSN about your counter"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                className="mt-2"
                data-testid="quote-counter-message"
              />
              <div className="mt-3 flex gap-2">
                <Button
                  onClick={submitCounter}
                  disabled={!!working}
                  className="rounded-full bg-fuchsia-700 hover:bg-fuchsia-800 text-white font-extrabold"
                  data-testid="quote-counter-submit"
                >
                  {working === "counter" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send counter-offer"}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full font-bold"
                  onClick={() => { setShowCounterBox(false); setCounterPrice(""); }}
                  disabled={!!working}
                  data-testid="quote-counter-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {(showRejectBox || showChangesBox) && (
            <div className="mt-4">
              <Textarea
                placeholder={showRejectBox ? "Optional — reason for rejection" : "What would you like to change?"}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                data-testid="quote-message-input"
              />
              <Button
                className="mt-2 rounded-full"
                onClick={() => act(showRejectBox ? "reject" : "changes")}
                disabled={!!working}
                data-testid="quote-message-submit"
              >
                {working ? <Loader2 className="w-4 h-4 animate-spin" /> : showRejectBox ? "Confirm rejection" : "Send change request"}
              </Button>
            </div>
          )}
        </div>
      )}

      {!showPayCTA && (status === "rejected" || status === "expired") && (
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-3xl p-6 text-center">
          {status === "rejected" ? (
            <><XCircle className="w-10 h-10 mx-auto text-rose-500" /><div className="mt-2 text-xl font-extrabold text-gray-900">Quote rejected</div></>
          ) : (
            <><Clock className="w-10 h-10 mx-auto text-gray-400" /><div className="mt-2 text-xl font-extrabold text-gray-900">Quote expired</div></>
          )}
        </div>
      )}
    </div>
  );
}
